// KIADÁS-űrlap tiszta segédei (nem-komponens): form-értékek, alapértékek, a `@atlas/contracts` write-sémája a
// resolverhez, és a lokalizált zod-hibatérkép. A validálás forrás-igazsága a CONTRACT (nem saját séma).
// Külön fájl a komponenstől (react-refresh tisztaság).

import {
  ExpenseCategoryWriteBodySchema,
  ExpenseWriteBodySchema,
  type ExpenseCategoryRow,
  type ExpenseCategoryWriteBody,
  type ExpenseImportDraft,
  type ExpenseRow,
  type ExpenseWriteBody,
} from '@atlas/contracts';
import { addDecimals } from '@atlas/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

/** A „nincs kategória" sentinel a selectben (a Radix Select nem enged üres `value`-t). */
export const NO_CATEGORY = 'none';

/**
 * A „bizonylat szerint" sentinel a vonatkozási hónap selectjében — ugyanaz a jelentés, mint a lista-cellában:
 * ne tárolj kézi értéket, a hónap a bizonylat dátumaiból származzon (a szerverre `null` megy).
 */
export const DERIVED_MONTH = 'derived';

export interface ExpenseFormValues {
  /** MELYIK CÉGÜNK költsége (`MOR` · `MDIG`). Kötelező: a kibocsátó-szűrő e nélkül nem találná meg a sort. */
  issuerKey: string;
  supplierName: string;
  supplierTaxNumber: string;
  invoiceNumber: string;
  /**
   * NINCS SZÁMLA (nyugta, bizonylat nélküli költség) — ilyenkor a számlaszám elhagyható, és a dróton `null`
   * megy ki. NEM külön tárolt mező: a szerveren a hiányzó számlaszám MAGA a jelzés.
   *
   * A KIÁLLÍTÁS DÁTUMA ettől KÖTELEZŐ MARAD: a vonatkozási hónap (kézi hónap híján) ebből származik, és
   * ezen áll a lap összege meg az eredménykimutatás — dátum nélkül a sor egyetlen hónaphoz sem tartozna.
   */
  noBill: boolean;
  issueDate: string;
  fulfillmentDate: string;
  dueDate: string;
  netAmount: string;
  vatAmount: string;
  currency: string;
  categoryId: string;
  /** A MUNKATÁRS azonosítója, vagy üres string = nincs személyhez kötve. */
  employeeIdentityId: string;
  /**
   * A VONATKOZÁSI HÓNAP (`YYYY-MM`), vagy `DERIVED_MONTH` → a bizonylatból származzon.
   *
   * MIÉRT MÁR A FELVITELKOR: ha a kezelő már a rögzítéskor tudja, hogy a tétel egy korábbi hónapot terhel
   * (pl. januárban érkező decemberi bérleti díj), ne kelljen utólag megkeresnie a listában (user-kérés).
   */
  accountingMonth: string;
  note: string;
}

export const EMPTY_EXPENSE_FORM: ExpenseFormValues = {
  // ALAPÉRTELMEZÉS a MOR: élesben a kiadások túlnyomó része MORGENS Design-é (a NAV-import is onnan jött),
  // tehát a gyakori eset az alapállapot — a ritkábbat a kezelő tudatosan választja.
  issuerKey: 'MOR',
  supplierName: '',
  supplierTaxNumber: '',
  invoiceNumber: '',
  noBill: false,
  issueDate: '',
  fulfillmentDate: '',
  dueDate: '',
  netAmount: '',
  vatAmount: '0',
  currency: 'HUF',
  categoryId: NO_CATEGORY,
  employeeIdentityId: '',
  accountingMonth: DERIVED_MONTH,
  note: '',
};

/** A szerkesztendő sor → form-értékek (a `null` mezők üres stringgé). */
export function expenseFormFrom(row: ExpenseRow): ExpenseFormValues {
  return {
    issuerKey: row.issuerKey,
    supplierName: row.supplierName,
    supplierTaxNumber: row.supplierTaxNumber ?? '',
    invoiceNumber: row.invoiceNumber ?? '',
    noBill: row.invoiceNumber === null,
    issueDate: row.issueDate,
    fulfillmentDate: row.fulfillmentDate ?? '',
    dueDate: row.dueDate ?? '',
    netAmount: row.netAmount,
    vatAmount: row.vatAmount,
    currency: row.currency,
    categoryId: row.categoryId ?? NO_CATEGORY,
    employeeIdentityId: row.employeeIdentityId ?? '',
    // A KÉZI értéket töltjük vissza, a származtatottat NEM: szerkesztéskor a „bizonylat szerint" ugyanazt
    // jelenti, mint felvitelkor — és egy származtatott hónap visszaírása KÉZIVÉ merevítené a sort.
    accountingMonth: row.accountingMonthManual ? row.accountingMonth : DERIVED_MONTH,
    note: row.note ?? '',
  };
}

/**
 * MÁSOLAT → form-értékek: a meglévő kézi kiadás minden adata, a SZÁMLASZÁM nélkül.
 *
 * MIÉRT ÜRES a számlaszám: a másolás a VISSZATÉRŐ költség felvitelét gyorsítja (havi bérleti díj,
 * előfizetés) — az új bizonylatnak saját száma van. Átvéve a mentés a duplikátum-falba ütközne (adószám +
 * számlaszám → 409), és ami rosszabb: adószám nélküli szállítónál CSENDBEN átmenne egy hamis számlaszámmal.
 * A „nincs számla" jelölés viszont öröklődik — a nyugta másolata is nyugta.
 */
export function expenseFormCopyOf(row: ExpenseRow): ExpenseFormValues {
  return { ...expenseFormFrom(row), invoiceNumber: '' };
}

/**
 * WIRE-BODY → form-értékek. A `expenseFormFrom` párja a TÖMEGES IMPORT számára: ott nincs mentett sor, csak
 * egy feloldott body, amit a kezelő ugyanazon az űrlapon javít. Így az import-javítás BÁJTRA ugyanazt a
 * mező-készletet és validációt kapja, mint a kézi felvitel — egy második űrlap elcsúszna tőle.
 */
export function expenseFormFromBody(body: ExpenseWriteBody): ExpenseFormValues {
  return {
    issuerKey: body.issuerKey,
    supplierName: body.supplierName,
    supplierTaxNumber: body.supplierTaxNumber ?? '',
    invoiceNumber: body.invoiceNumber ?? '',
    noBill: body.invoiceNumber === null,
    issueDate: body.issueDate,
    fulfillmentDate: body.fulfillmentDate ?? '',
    dueDate: body.dueDate ?? '',
    netAmount: body.netAmount,
    vatAmount: body.vatAmount,
    currency: body.currency,
    categoryId: body.categoryId ?? NO_CATEGORY,
    employeeIdentityId: body.employeeIdentityId ?? '',
    accountingMonth: body.accountingMonth ?? DERIVED_MONTH,
    note: body.note ?? '',
  };
}

/**
 * IMPORT-VÁZLAT → form-értékek. A tömeges import JAVÍTÓ-űrlapja ezt tölti be.
 *
 * MIÉRT KÜLÖN a `expenseFormFromBody`-tól: a vázlat a HIBÁS sort is leírja, tehát a nem értelmezhető mezők
 * `null`-ok. Az űrlap üres mezőként mutatja őket — a TÖBBI érték viszont ott van, és nem kell újra begépelni
 * azt, amit a táblázat és az előnézet is mutat.
 */
export function expenseFormFromDraft(draft: ExpenseImportDraft): ExpenseFormValues {
  return {
    issuerKey: draft.issuerKey === '' ? 'MOR' : draft.issuerKey,
    supplierName: draft.supplierName,
    supplierTaxNumber: draft.supplierTaxNumber ?? '',
    invoiceNumber: draft.invoiceNumber ?? '',
    noBill: draft.noBill,
    issueDate: draft.issueDate ?? '',
    fulfillmentDate: draft.fulfillmentDate ?? '',
    dueDate: draft.dueDate ?? '',
    netAmount: draft.netAmount ?? '',
    vatAmount: draft.vatAmount ?? '0',
    currency: draft.currency === '' ? 'HUF' : draft.currency,
    categoryId: draft.categoryId ?? NO_CATEGORY,
    employeeIdentityId: draft.employeeIdentityId ?? '',
    accountingMonth: draft.accountingMonth ?? DERIVED_MONTH,
    note: draft.note ?? '',
  };
}

/** A tizedes-vessző elfogadása: a magyar billentyűzeten az a természetes elválasztó. */
function decimalOf(value: string): string {
  const normalized = value.trim().replace(',', '.');
  return normalized === '' ? '' : normalized;
}

/** Form-értékek → wire-body. Üres opcionális mező → `null`; a bruttó a nettó + áfa. */
export function toExpenseBody(v: ExpenseFormValues): ExpenseWriteBody {
  const nn = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  const net = decimalOf(v.netAmount);
  const vat = decimalOf(v.vatAmount);
  return {
    issuerKey: v.issuerKey as ExpenseWriteBody['issuerKey'],
    supplierName: v.supplierName.trim(),
    supplierTaxNumber: nn(v.supplierTaxNumber),
    // A jelölés ERŐSEBB a beírt szövegnél: ha „nincs számla", a mezőben maradt szám nem szivároghat ki.
    invoiceNumber: v.noBill ? null : nn(v.invoiceNumber),
    issueDate: v.issueDate,
    fulfillmentDate: nn(v.fulfillmentDate),
    dueDate: nn(v.dueDate),
    netAmount: net,
    vatAmount: vat === '' ? '0' : vat,
    grossAmount: addDecimals(net, vat),
    currency: v.currency,
    categoryId: v.categoryId === NO_CATEGORY ? null : v.categoryId,
    employeeIdentityId: v.employeeIdentityId === '' ? null : v.employeeIdentityId,
    accountingMonth: v.accountingMonth === DERIVED_MONTH ? null : v.accountingMonth,
    note: nn(v.note),
  };
}

/** Lokalizált zod-hibatérkép: mező (`issue.path[0]`) → a `validation:expenses.*` réteg. */
export function buildExpenseErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'supplierName') return { message: t('validation:expenses.supplierName') };
    if (field === 'invoiceNumber') return { message: t('validation:expenses.invoiceNumber') };
    if (field === 'issueDate') return { message: t('validation:expenses.issueDate') };
    if (field === 'netAmount' || field === 'vatAmount' || field === 'grossAmount') {
      return { message: t('validation:expenses.amount') };
    }
    if (field === 'currency') return { message: t('validation:expenses.currency') };
    if (field === 'issuerKey') return { message: t('validation:expenses.issuerKey') };
    if (field === 'accountingMonth') return { message: t('validation:expenses.accountingMonth') };
    return { message: ctx.defaultError };
  };
}

/**
 * A resolver a CONTRACT sémáján validál, de a form ALAKJÁN — ezért a form-értékeket előbb wire-body-ra
 * képezzük. Így a tizedes-vessző és a „bruttó = nettó + áfa" szabály is a validálás előtt érvényesül.
 */
export function buildExpenseResolver(t: TFunction): Resolver<ExpenseFormValues> {
  const base = zodResolver(ExpenseWriteBodySchema, {
    errorMap: buildExpenseErrorMap(t),
  }) as unknown as Resolver<ExpenseFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toExpenseBody(values) as unknown as ExpenseFormValues,
      context,
      options,
    );
    // A hiba-mezőnevek a body és a form között AZONOSAK — kivéve a SZÁMÍTOTT bruttót: annak a hibáját a
    // nettó mezőre kötjük, különben „láthatatlan" hiba lenne (nincs hozzá mező, amin megjelenhetne).
    const errors: Record<string, unknown> = { ...result.errors };
    if ('grossAmount' in errors) {
      errors.netAmount ??= errors.grossAmount;
      delete errors.grossAmount;
    }
    // A SZÁMLASZÁM FELTÉTELES kötelezősége CSAK itt él, nem a contractban — és ez nem rés, hanem a modell:
    // a dróton a „nincs számla" maga a `null`, tehát a szerver nem tudná megkülönböztetni a szándékos
    // elhagyást az elfelejtett kitöltéstől. A jelölés a kezelő SZÁNDÉK-nyilatkozata, ezért a felületé.
    if (!values.noBill && values.invoiceNumber.trim() === '') {
      errors.invoiceNumber ??= {
        type: 'required',
        message: t('validation:expenses.invoiceNumber'),
      };
    }
    if (Object.keys(errors).length > 0) {
      return { values: {}, errors: errors as typeof result.errors };
    }
    return { values, errors: {} };
  };
}

// ── Kategória-űrlap ─────────────────────────────────────────────────────────────────────────────────

/** A „fő-kategória" sentinel a szülő-selectben (üres `value`-t a Radix Select nem enged). */
export const ROOT_PARENT = 'root';

export interface CategoryFormValues {
  nameHu: string;
  nameEn: string;
  /** `ROOT_PARENT` → főkategória. A fa LEGFELJEBB HÁROM szintű, ezért szülőnek a 0. és 1. szint kínálható. */
  parentId: string;
  /**
   * BÉR TÍPUSÚ TÉTEL: az ide sorolt költségnél a felület felkínálja a munkatárs megadását. CSAK tételen
   * (levélen) állhat, és NEM öröklődik.
   */
  requiresEmployee: boolean;
}

export const EMPTY_CATEGORY_FORM: CategoryFormValues = {
  nameHu: '',
  nameEn: '',
  parentId: ROOT_PARENT,
  requiresEmployee: false,
};

export function categoryFormFrom(row: ExpenseCategoryRow): CategoryFormValues {
  return {
    nameHu: row.name.hu,
    nameEn: row.name.en ?? '',
    parentId: row.parentId ?? ROOT_PARENT,
    requiresEmployee: row.requiresEmployee,
  };
}

export function toCategoryBody(v: CategoryFormValues): ExpenseCategoryWriteBody {
  const en = v.nameEn.trim();
  return {
    name: { hu: v.nameHu.trim(), en: en === '' ? null : en },
    parentId: v.parentId === ROOT_PARENT ? null : v.parentId,
    requiresEmployee: v.requiresEmployee,
  };
}

/** Lokalizált zod-hibatérkép a kategória-űrlaphoz (a név-hiba a `nameHu` mezőre kerül). */
export function buildCategoryErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    if (issue.path[0] === 'name') return { message: t('validation:expenses.categoryName') };
    return { message: ctx.defaultError };
  };
}

export function buildCategoryResolver(t: TFunction): Resolver<CategoryFormValues> {
  const base = zodResolver(ExpenseCategoryWriteBodySchema, {
    errorMap: buildCategoryErrorMap(t),
  }) as unknown as Resolver<CategoryFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toCategoryBody(values) as unknown as CategoryFormValues,
      context,
      options,
    );
    // A contract a nevet BEÁGYAZOTT objektumként validálja (`name.hu`) — a form KÉT lapos mezőt ismer,
    // ezért a hibát a magyar név mezőjére kötjük (az a kötelező).
    const errors: Record<string, unknown> = { ...result.errors };
    if ('name' in errors) {
      errors.nameHu ??= errors.name;
      delete errors.name;
    }
    if (Object.keys(errors).length > 0) {
      return { values: {}, errors: errors as typeof result.errors };
    }
    return { values, errors: {} };
  };
}
